class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.headerCartLinks = new WeakSet();
    this.onHeaderSectionLoad = () => this.setHeaderCartIconAccessibility();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegated, so it keeps working after the drawer markup is re-rendered.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close], #CartDrawer-Overlay')) this.close();
    });
    // One overlay at a time: the menu, search and localization drawers close this one and vice versa.
    document.addEventListener('kvs:overlay-open', (event) => {
      if (event.detail?.source !== this && this.classList.contains('active')) this.close(false);
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    document.addEventListener('shopify:section:load', this.onHeaderSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.onHeaderSectionLoad);
    clearTimeout(this.openTimer);
    clearTimeout(this.renderOpenTimer);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    if (this.headerCartLinks.has(cartLink)) return;
    this.headerCartLinks.add(cartLink);
    cartLink.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    document.dispatchEvent(new CustomEvent('kvs:overlay-open', { detail: { source: this } }));
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      this.focusContents();
    });

    document.body.classList.add('overflow-hidden');
  }

  close(restoreFocus = true) {
    clearTimeout(this.openTimer);
    clearTimeout(this.renderOpenTimer);
    this.classList.remove('active');
    removeTrapFocus(restoreFocus ? this.activeElement : null);
    document.body.classList.remove('overflow-hidden');
  }

  focusContents() {
    if (!this.isConnected || !this.classList.contains('active')) return;
    const container = this.querySelector('.drawer__inner-empty') || this.querySelector('#CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (container && focusElement) trapFocus(container, focusElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    clearTimeout(this.renderOpenTimer);
    this.renderOpenTimer = setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
